import type {
  BucketId,
  NormalStep,
  PhaseBucket,
  PhaseGrouping,
  PhaseSection,
} from './steps.types';

/** Display order + labels of the three portal phases. Index 0 is also the
 *  fallback bucket for unknown/absent journey positions. */
export const PHASE_BUCKETS: readonly PhaseBucket[] = [
  { id: 'apply', label: 'Apply' },
  { id: 'documents_compliance', label: 'Documents & Compliance' },
  { id: 'visa_travel', label: 'Visa & Travel' },
];

/**
 * Globus journey position -> portal phase bucket.
 *
 * The 8 canonical positions come from BOP `shared/phaseApplicability.ts`
 * (JOURNEY_POSITIONS), plus the display-only `renewal_extension`.
 *
 * ⚠️ OPEN PRODUCT QUESTION — `placement`.
 * `placement` is mapped to Documents & Compliance, NOT Visa & Travel. This is a
 * DEFAULT, not a settled product decision, and it is called out in the PR that
 * introduced this table so product can overrule it with one line here:
 *   • for Documents & Compliance — the applicant-facing work during placement is
 *     interview/document driven, and placement strictly precedes any visa action;
 *   • for Apply / Visa & Travel — placement is a sponsor/host matching gate the
 *     applicant does no paperwork for, and it immediately gates DS-2019 issuance,
 *     so applicants may perceive it as the start of the visa run.
 * `renewal_extension` maps to Apply because a renewal restarts the apply funnel.
 */
export const BUCKET_BY_JOURNEY_POSITION: Readonly<Record<string, BucketId>> = {
  application: 'apply',
  validation: 'apply',
  interview: 'documents_compliance',
  placement: 'documents_compliance', // see the OPEN PRODUCT QUESTION above
  visa_sevis: 'visa_travel',
  pre_departure: 'visa_travel',
  in_program: 'visa_travel',
  completion: 'visa_travel',
  renewal_extension: 'apply',
};

/** Journey positions we have already warned about, so an unmapped key logs once
 *  per session instead of once per render. */
const warnedJourneyPositions = new Set<string>();

/** Partial-coverage warn-once latch (see groupSteps). */
let warnedPartialCoverage = false;

/**
 * Test-only: clear the module-level warn-once latches.
 *
 * These guards are module state that survives between tests, which makes any
 * "warned exactly once" assertion order-dependent (it passes only while each
 * key happens to appear in a single test). Call this from `beforeEach`.
 */
export function resetJourneyPositionWarnings(): void {
  warnedJourneyPositions.clear();
  warnedPartialCoverage = false;
}

/**
 * The portal phase a step belongs to.
 *
 * An UNKNOWN or ABSENT journey position resolves to the FIRST bucket rather than
 * being dropped: dropping a step would silently remove work from the applicant's
 * list, which is strictly worse than showing it under the wrong heading. Unknown
 * (but present) keys warn once so a new Globus position surfaces in the console
 * instead of failing silently.
 *
 * ABSENCE deliberately does NOT warn here: this function sees ONE step, so it
 * cannot tell benign absence (nothing authored anywhere — the normal state on
 * dev) from partial-authoring drift (some steps stamped, some not). That
 * discrimination needs the whole array and therefore lives in groupSteps.
 */
export function bucketOf(step: Pick<NormalStep, 'journeyPositionKey'>): BucketId {
  const key = step.journeyPositionKey;
  if (!key) return PHASE_BUCKETS[0].id;
  const bucket = BUCKET_BY_JOURNEY_POSITION[key];
  if (bucket) return bucket;
  if (!warnedJourneyPositions.has(key)) {
    warnedJourneyPositions.add(key);
    console.warn(
      `[steps] unmapped Globus journeyPositionKey "${key}" — rendering it under "${PHASE_BUCKETS[0].label}". ` +
        'Add it to BUCKET_BY_JOURNEY_POSITION.',
    );
  }
  return PHASE_BUCKETS[0].id;
}

/**
 * Group steps into the three portal phases, in PHASE_BUCKETS order.
 * Empty buckets are dropped — matching the pre-bucket behaviour (only phases
 * with steps ever rendered) and avoiding a zero-step tab. Flows that mark whole
 * positions `n_a` (e.g. the extension flow skips interview + placement) can
 * legitimately produce fewer than three sections.
 */
export function groupByBucket(steps: NormalStep[]): PhaseSection[] {
  const byBucket = new Map<BucketId, NormalStep[]>();
  steps.forEach((s) => {
    const id = bucketOf(s);
    if (!byBucket.has(id)) byBucket.set(id, []);
    byBucket.get(id)!.push(s);
  });
  return PHASE_BUCKETS.filter((b) => byBucket.has(b.id)).map(
    (b) => [b.id, { name: b.label, steps: byBucket.get(b.id)! }] as PhaseSection,
  );
}

/** How many of an application's steps Globus has stamped a journey position on. */
export function journeyPositionCoverage(steps: NormalStep[]): { populated: number; total: number } {
  return { populated: steps.filter((s) => Boolean(s.journeyPositionKey)).length, total: steps.length };
}

/**
 * True only when EVERY step carries a journey position — the condition for
 * switching an application to the 3-bucket grouping.
 *
 * Full coverage, not `some`: `bucketOf` sends an unstamped step to bucket 0 by
 * design, so a partially-stamped application would bucket its stamped steps
 * correctly and dump every unstamped one under "Apply" — silently mislabelling
 * most of the application (and reordering Save & Continue with it). Requiring
 * full coverage is also monotone: it flips exactly once, when authoring
 * completes, so the PR's "no visible change until the data exists" promise holds
 * for PARTIAL authoring too, not just for zero.
 */
export function hasFullJourneyCoverage(steps: NormalStep[]): boolean {
  const { populated, total } = journeyPositionCoverage(steps);
  return total > 0 && populated === total;
}

/**
 * True only when EVERY step carries authored phase metadata (phases[].name +
 * phaseOrder from the published workflow's program-config, joined by the BFF)
 * — the condition for rendering the AUTHORED phase structure. Full coverage
 * for the same reason as hasFullJourneyCoverage: a partially-joined list would
 * confidently mislabel the un-joined steps.
 */
export function hasAuthoredPhases(steps: NormalStep[]): boolean {
  return (
    steps.length > 0 &&
    steps.every((s) => s.authoredPhaseName === true && typeof s.phaseOrder === 'number')
  );
}

export const phaseGroupingOf = (steps: NormalStep[]): PhaseGrouping =>
  hasAuthoredPhases(steps) ? 'authored' : hasFullJourneyCoverage(steps) ? 'bucket' : 'legacy';

/**
 * Group steps into the sections the portal renders.
 *
 * Bucketed into the 3 portal phases only when EVERY step carries a journey
 * position. Otherwise we keep the legacy one-section-per-`phaseKey` grouping
 * instead of collapsing the application into a single "Apply" tab — verified on
 * dev 2026-07-30, where `workflow_steps.journey_position_key` is unauthored for
 * every step of both E2E bot applications (0/13 and 0/20; 180/1875 rows
 * populated workspace-wide). Collapsing those applications to one tab would be a
 * visible regression, so the 3-phase contract activates per-application, the
 * moment its data is COMPLETE.
 *
 * PARTIAL coverage (some steps stamped, some not) is the drift case and is the
 * one place it can be detected — the whole array is in scope here, whereas
 * `bucketOf` sees one step and structurally cannot tell benign absence from
 * drift. It warns once and stays on the legacy grouping.
 */
export function groupSteps(steps: NormalStep[]): PhaseSection[] {
  // AUTHORED grouping first (Asana 1217121013432648): when every step carries
  // the published workflow's phase metadata, render the authored phases —
  // grouped by phaseKey, labelled by phases[].name, ordered by phaseOrder.
  // The portal must never invent phase names when the authored ones exist,
  // so this takes precedence over the 3-bucket contract below.
  if (hasAuthoredPhases(steps)) {
    const byPhase = new Map<string, { name: string; order: number; steps: NormalStep[] }>();
    steps.forEach((s) => {
      if (!byPhase.has(s.phaseKey)) {
        byPhase.set(s.phaseKey, { name: s.phaseName, order: s.phaseOrder as number, steps: [] });
      }
      byPhase.get(s.phaseKey)!.steps.push(s);
    });
    return [...byPhase.entries()]
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([k, { name, steps: sec }]) => [k, { name, steps: sec }] as PhaseSection);
  }
  const { populated, total } = journeyPositionCoverage(steps);
  if (total > 0 && populated === total) return groupByBucket(steps);
  if (populated > 0 && !warnedPartialCoverage) {
    warnedPartialCoverage = true;
    console.warn(
      `[steps] PARTIAL Globus journey-position coverage (${populated} of ${total} steps stamped) — ` +
        'staying on the legacy per-phaseKey grouping. Bucketing needs every step stamped, or the ' +
        'unstamped ones would all render under "' +
        PHASE_BUCKETS[0].label +
        '". Finish authoring journey_position_key for this workflow.',
    );
  }
  const byPhase = new Map<string, { name: string; steps: NormalStep[] }>();
  steps.forEach((s) => {
    if (!byPhase.has(s.phaseKey)) byPhase.set(s.phaseKey, { name: s.phaseName, steps: [] });
    byPhase.get(s.phaseKey)!.steps.push(s);
  });
  return [...byPhase.entries()];
}

/** The section that actually contains `step` — the only safe way to select a
 *  tab when navigating to a step, since section keys are bucket ids in one mode
 *  and phaseKeys in the other. */
export function sectionKeyOf(sections: PhaseSection[], step: NormalStep): string | undefined {
  return sections.find(([, sec]) => sec.steps.some((s) => s.stepKey === step.stepKey))?.[0];
}

/**
 * Resolve a deep-link's requested section, in both directions.
 *
 * Deep-links are minted by DashboardPage / ProgressBanner and read here. Before
 * bucketing they carried a raw `phaseKey`; they now also carry a `bucketId`.
 * Links already in flight (bookmarks, notification-inbox entries) carry only the
 * old value, so an exact-key miss must NOT fall through to tab 1 silently:
 *  1. exact section-key match (bucket id, or phaseKey in legacy mode);
 *  2. legacy journey-position value -> its bucket;
 *  3. legacy phaseKey -> whichever section now CONTAINS those steps;
 *  4. new bucket id -> a section whose steps ARE STAMPED with that bucket.
 * Returns undefined only when genuinely unresolvable, so the caller's
 * `?? sections[0]` stays a last resort rather than the normal path.
 */
export function resolvePhaseSection(
  sections: PhaseSection[],
  requested: string | undefined,
): PhaseSection | undefined {
  if (!requested || sections.length === 0) return undefined;
  const exact = sections.find(([k]) => k === requested);
  if (exact) return exact;
  const mapped = BUCKET_BY_JOURNEY_POSITION[requested];
  if (mapped) {
    const viaPosition = sections.find(([k]) => k === mapped);
    if (viaPosition) return viaPosition;
  }
  const viaSteps = sections.find(([, sec]) =>
    sec.steps.some((s) => s.phaseKey === requested || s.journeyPositionKey === requested),
  );
  if (viaSteps) return viaSteps;
  if (PHASE_BUCKETS.some((b) => b.id === requested)) {
    // Only a step that actually CARRIES a journey position can vouch for a
    // bucket. Matching on `bucketOf` alone made this step fire in legacy mode
    // for `apply` — every unstamped step buckets to PHASE_BUCKETS[0], so it
    // returned sections[0]: indistinguishable from the caller's own
    // `?? sections[0]`, but dressed up as a successful resolution. Step 4 must
    // not fire when it cannot do better than the fallback.
    const viaBucket = sections.find(([, sec]) =>
      sec.steps.some((s) => Boolean(s.journeyPositionKey) && bucketOf(s) === requested),
    );
    if (viaBucket) return viaBucket;
  }
  return undefined;
}

/**
 * Router state for a deep-link to `step`. Emits the bucket id AND the legacy
 * phaseKey so the consumer resolves correctly in either grouping mode (see
 * resolvePhaseSection). `bucketId` is omitted when Globus has authored no
 * journey position for the step — in that mode every step buckets to `apply`,
 * which would be an ambiguous target.
 *
 * `stepKey` is always present and is the primary key the consumer resolves from:
 * it is exact and grouping-mode independent, so it is immune to the bucket
 * migration entirely (see ApplicationPage's deep-link resolution).
 */
export function deepLinkStateFor(step: NormalStep): {
  stepKey: string;
  phaseKey: string;
  bucketId?: BucketId;
} {
  return {
    stepKey: step.stepKey,
    phaseKey: step.phaseKey,
    ...(step.journeyPositionKey ? { bucketId: bucketOf(step) } : {}),
  };
}
